import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAdminProduct, createProduct, updateProduct, getCategories, Category } from '../api/products.api';
import LoadingSpinner from '../components/LoadingSpinner';
import ProductImage from '../components/ProductImage';

const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

const AdminProductFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    price: '',
    available_sizes: [] as string[],
    stock_quantity: '',
    is_active: true,
  });
  const [productImage, setProductImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategories();
        setCategories(response.data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();

    if (isEditMode && id) {
      const fetchProduct = async () => {
        try {
          const response = await getAdminProduct(id);
          const product = response.data;
          setFormData({
            name: product.name || '',
            description: product.description || '',
            category_id: product.category_id || product.category?.id || '',
            price: product.price?.toString() || '',
            available_sizes: product.available_sizes || [],
            stock_quantity: product.stock_quantity?.toString() || '',
            is_active: product.is_active ?? true,
          });
          if (product.image_url) {
            setImagePreview(product.image_url);
          }
        } catch (err) {
          console.error('Error fetching product:', err);
          setErrors({ general: 'Failed to load product. Please try again.' });
        } finally {
          setLoading(false);
        }
      };

      fetchProduct();
    } else {
      setLoading(false);
    }
  }, [id, isEditMode]);

  const handleChange = (field: string, value: string | boolean | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSizeToggle = (size: string) => {
    setFormData((prev) => {
      const sizes = prev.available_sizes.includes(size)
        ? prev.available_sizes.filter((s) => s !== size)
        : [...prev.available_sizes, size];
      return { ...prev, available_sizes: sizes };
    });
    if (errors.available_sizes) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.available_sizes;
        return newErrors;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setErrors({ ...errors, image: 'File size exceeds 5MB limit.' });
        e.target.value = '';
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setErrors({ ...errors, image: 'Invalid file type. Please upload JPEG, PNG, GIF, or WEBP image.' });
        e.target.value = '';
        return;
      }
      setProductImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.image;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required.';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required.';
    }

    if (!formData.price || parseFloat(formData.price) < 0) {
      newErrors.price = 'Valid price is required (must be >= 0).';
    }

    if (formData.stock_quantity && parseInt(formData.stock_quantity) < 0) {
      newErrors.stock_quantity = 'Stock quantity must be >= 0.';
    }

    if (!isEditMode && !productImage) {
      newErrors.image = 'Product image is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const formDataToSend = new FormData();

      formDataToSend.append('name', formData.name);
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }
      formDataToSend.append('category_id', formData.category_id);
      formDataToSend.append('price', formData.price);
      // Send available_sizes as multiple fields with same key (standard FormData array format)
      // Only send if there are sizes selected - backend will default to empty array if not provided
      if (formData.available_sizes.length > 0) {
        formData.available_sizes.forEach((size) => {
          formDataToSend.append('available_sizes', size);
        });
      }
      // Don't send available_sizes at all if empty - backend defaults to [] when optional
      if (formData.stock_quantity) {
        formDataToSend.append('stock_quantity', formData.stock_quantity);
      }
      formDataToSend.append('is_active', formData.is_active.toString());

      if (productImage) {
        formDataToSend.append('image', productImage);
      }

      if (isEditMode && id) {
        await updateProduct(id, formDataToSend);
      } else {
        await createProduct(formDataToSend);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard/admin/products');
      }, 1500);
    } catch (err: any) {
      if (err.response?.status === 400) {
        const errorData = err.response?.data;
        if (errorData?.errors) {
          setErrors(errorData.errors);
        } else {
          setErrors({ general: errorData?.message || 'Invalid data. Please check your inputs.' });
        }
      } else if (err.response?.status === 401) {
        setErrors({ general: 'Your session has expired. Please log in again.' });
        setTimeout(() => {
          navigate('/auth');
        }, 2000);
      } else if (err.response?.status === 413) {
        setErrors({ image: 'File is too large. Maximum size is 5MB.' });
      } else {
        setErrors({ general: err.response?.data?.message || 'Failed to save product. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="card card-surface shadow-sm">
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h1 className="h4 mb-0">{isEditMode ? 'Edit Product' : 'Add New Product'}</h1>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => navigate('/dashboard/admin/products')}
                >
                  <i className="bi bi-arrow-left me-1"></i>
                  Back
                </button>
              </div>

              {success && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <i className="bi bi-check-circle me-2"></i>
                  Product {isEditMode ? 'updated' : 'created'} successfully! Redirecting...
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSuccess(false)}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              {errors.general && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {errors.general}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      const newErrors = { ...errors };
                      delete newErrors.general;
                      setErrors(newErrors);
                    }}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="row g-3">
                {/* Product Image */}
                <div className="col-12">
                  <label className="form-label">
                    Product Image {!isEditMode && <span className="text-danger">*</span>}
                  </label>
                  <div className="d-flex flex-column flex-md-row align-items-start gap-3">
                    {imagePreview && (
                      <div className="flex-shrink-0">
                        <ProductImage
                          src={imagePreview}
                          alt="Product preview"
                          height={200}
                          width={200}
                          style={{ borderRadius: '4px' }}
                        />
                      </div>
                    )}
                    <div className="flex-grow-1">
                      <input
                        type="file"
                        className={`form-control ${errors.image ? 'is-invalid' : ''}`}
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={saving}
                      />
                      {errors.image && <div className="invalid-feedback">{errors.image}</div>}
                      <div className="form-text">Max 5MB. Formats: JPEG, PNG, GIF, WEBP</div>
                    </div>
                  </div>
                </div>

                {/* Product Name */}
                <div className="col-md-6">
                  <label className="form-label">
                    Product Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    disabled={saving}
                    maxLength={255}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                {/* Category */}
                <div className="col-md-6">
                  <label className="form-label">
                    Category <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.category_id ? 'is-invalid' : ''}`}
                    value={formData.category_id}
                    onChange={(e) => handleChange('category_id', e.target.value)}
                    disabled={saving}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.category_id && <div className="invalid-feedback">{errors.category_id}</div>}
                </div>

                {/* Description */}
                <div className="col-12">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    disabled={saving}
                  />
                </div>

                {/* Price */}
                <div className="col-md-6">
                  <label className="form-label">
                    Price (BDT) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    disabled={saving}
                  />
                  {errors.price && <div className="invalid-feedback">{errors.price}</div>}
                </div>

                {/* Stock Quantity */}
                <div className="col-md-6">
                  <label className="form-label">Stock Quantity</label>
                  <input
                    type="number"
                    min="0"
                    className={`form-control ${errors.stock_quantity ? 'is-invalid' : ''}`}
                    value={formData.stock_quantity}
                    onChange={(e) => handleChange('stock_quantity', e.target.value)}
                    disabled={saving}
                  />
                  {errors.stock_quantity && <div className="invalid-feedback">{errors.stock_quantity}</div>}
                </div>

                {/* Available Sizes */}
                <div className="col-12">
                  <label className="form-label">
                    Available Sizes
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {SIZE_OPTIONS.map((size) => (
                      <div key={size} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`size-${size}`}
                          checked={formData.available_sizes.includes(size)}
                          onChange={() => handleSizeToggle(size)}
                          disabled={saving}
                        />
                        <label className="form-check-label" htmlFor={`size-${size}`}>
                          {size}
                        </label>
                      </div>
                    ))}
                  </div>
                  {errors.available_sizes && <div className="text-danger small mt-1">{errors.available_sizes}</div>}
                </div>

                {/* Is Active */}
                <div className="col-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => handleChange('is_active', e.target.checked)}
                      disabled={saving}
                    />
                    <label className="form-check-label" htmlFor="is_active">
                      Product is active (visible to customers)
                    </label>
                  </div>
                </div>

                <div className="col-12">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => navigate('/dashboard/admin/products')}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-arena flex-grow-1" disabled={saving}>
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        isEditMode ? 'Update Product' : 'Create Product'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProductFormPage;
